import { useParams, useNavigate } from "react-router-dom";
import Badge, { type BadgeTone } from "@/components/base/Badge";
import { getTicketDetail, tickets } from "@/mocks/support";
import { useProvider } from "@/hooks/useProvider";

const statusTone: Record<string, BadgeTone> = {
  solved: "green",
  review: "amber",
  open: "blue",
};

const statusIcon = {
  solved: "ri-check-double-line",
  review: "ri-time-line",
  open: "ri-message-3-line",
};

const statusLabel: Record<string, string> = {
  solved: "تم الحل",
  review: "قيد المراجعة",
  open: "مفتوحة",
};

export default function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { provider, loading: providerLoading } = useProvider();

  const ticket = tickets.find((t) => t.id === ticketId);
  const detail = getTicketDetail(ticketId || "");

  if (providerLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#E4EFEF] border-t-[#0D6E6E]" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F0FDFD]">
          <i className="ri-error-warning-line text-2xl text-ghasla-text-secondary" />
        </div>
        <p className="text-sm font-medium text-ghasla-text-heading">التذكرة غير موجودة</p>
        <button
          type="button"
          onClick={() => navigate("/provider/support")}
          className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          <i className="ri-arrow-right-line" />
          العودة للدعم
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[980px]">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate("/provider/support")}
        className="mb-5 inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-ghasla-text-secondary transition-colors hover:text-ghasla-text-link"
      >
        <i className="ri-arrow-right-line text-lg" />
        العودة للتذاكر
      </button>

      {/* Header Card */}
      <div className="rounded-[20px] border border-[#F0F4F4] bg-white p-5 shadow-card md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F0FDFD]">
                <i className={`${statusIcon[ticket.status]} text-lg text-ghasla-text-link`} />
              </span>
              <h1 className="text-xl font-bold text-ghasla-text-heading md:text-[24px]" dir="ltr">
                {ticket.id}
              </h1>
              <Badge tone={statusTone[ticket.status]}>{ticket.statusLabel}</Badge>
            </div>
            <p className="mt-2 text-base font-semibold text-ghasla-text-heading">{detail.subject}</p>
            <p className="mt-1 text-sm text-ghasla-text-secondary">{ticket.type} · {ticket.date}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_320px]">
        {/* Main: Conversation */}
        <div className="space-y-4">
          <div className="rounded-[20px] border border-[#F0F4F4] bg-white p-5 shadow-card md:p-6">
            <h3 className="flex items-center gap-2 text-base font-semibold text-ghasla-text-heading">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#F0FDFD] text-ghasla-text-link">
                <i className="ri-message-3-line text-sm" />
              </span>
              المحادثة
            </h3>

            <div className="mt-5 space-y-4">
              {detail.conversation.length === 0 ? (
                <p className="rounded-xl bg-[#F0FDFD] p-4 text-sm text-ghasla-text-secondary">
                  لا توجد رسائل بعد — بنرد عليك قريباً.
                </p>
              ) : (
                detail.conversation.map((m) => (
                  <div
                    key={m.id}
                    className={m.author === "provider" ? "flex justify-start" : "flex justify-end"}
                  >
                    <div className="max-w-[85%] space-y-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs font-semibold ${
                            m.author === "provider"
                              ? "text-ghasla-text-link"
                              : "text-ghasla-text-secondary"
                          }`}
                        >
                          {m.authorName}
                        </span>
                        <span className="text-xs text-ghasla-text-secondary">{m.time}</span>
                      </div>
                      <div
                        className={`rounded-2xl px-4 py-3 ${
                          m.author === "provider"
                            ? "bg-[#0D6E6E] text-white"
                            : "bg-[#F0FDFD] text-ghasla-text-heading"
                        }`}
                      >
                        <p className="text-sm leading-relaxed">{m.text}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Reply box */}
            <div className="mt-5 rounded-[14px] border border-[#E4EFEF] bg-[#F0FDFD] p-4">
              <textarea
                className="min-h-[80px] w-full resize-none bg-transparent text-sm text-ghasla-text-heading outline-none placeholder:text-ghasla-text-secondary"
                placeholder="اكتب رداً..."
                maxLength={500}
              />
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-ghasla-text-secondary">0/500</span>
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                >
                  <i className="ri-send-plane-fill" />
                  إرسال
                </button>
              </div>
            </div>
          </div>

          {/* Attachments */}
          <div className="rounded-[20px] border border-[#F0F4F4] bg-white p-5 shadow-card md:p-6">
            <h3 className="flex items-center gap-2 text-base font-semibold text-ghasla-text-heading">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#F0FDFD] text-ghasla-text-link">
                <i className="ri-attachment-line text-sm" />
              </span>
              المرفقات
            </h3>
            <div className="mt-4">
              {detail.attachments.length === 0 ? (
                <p className="text-sm text-ghasla-text-secondary">لا توجد مرفقات</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {detail.attachments.map((a) => (
                    <div
                      key={a.name}
                      className="flex items-center gap-2 rounded-xl border border-[#E4EFEF] bg-[#F0FDFD] px-3 py-2"
                    >
                      <i className="ri-file-3-line text-ghasla-text-link" />
                      <span className="text-sm text-ghasla-text-heading">{a.name}</span>
                      <span className="text-xs text-ghasla-text-secondary">{a.size}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Resolution */}
          {detail.resolutionNote && (
            <div className="rounded-[20px] border border-[#E4EFEF] bg-[#E8F9F0] p-5 md:p-6">
              <h3 className="flex items-center gap-2 text-base font-semibold text-[#27AE60]">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#27AE60]/10 text-[#27AE60]">
                  <i className="ri-check-double-line text-sm" />
                </span>
                ملخص الحل
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-ghasla-text-heading">
                {detail.resolutionNote}
              </p>
            </div>
          )}
        </div>

        {/* Sidebar: Timeline */}
        <div className="rounded-[20px] border border-[#F0F4F4] bg-white p-5 shadow-card md:p-6 lg:sticky lg:top-4">
          <h3 className="flex items-center gap-2 text-base font-semibold text-ghasla-text-heading">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#F0FDFD] text-ghasla-text-link">
              <i className="ri-history-line text-sm" />
            </span>
            مسار الحالة
          </h3>

          <div className="mt-5 space-y-0">
            {detail.timeline.map((e, i) => {
              const isLast = i === detail.timeline.length - 1;
              return (
                <div key={e.id} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
                        e.done
                          ? "bg-[#0D6E6E] text-white"
                          : "border-2 border-[#E4F5F5] bg-white text-ghasla-text-secondary"
                      }`}
                    >
                      {e.done ? <i className="ri-check-line" /> : <i className="ri-time-line" />}
                    </span>
                    {!isLast && (
                      <span className={`w-px flex-1 ${e.done ? "bg-[#0D6E6E]" : "bg-[#E4F5F5]"}`} />
                    )}
                  </div>
                  <div className="pb-5">
                    <p className={`text-sm ${e.done ? "font-medium text-ghasla-text-heading" : "text-ghasla-text-secondary"}`}>
                      {e.label}
                    </p>
                    <p className="text-xs text-ghasla-text-secondary">{e.time}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}